import { useState, useEffect } from 'react';
import { useProfile } from './useProfile';
import { db, auth } from '../lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

export interface DailyDeeds {
  [date: string]: number; // "YYYY-MM-DD": count
}

export interface HablTransaction {
  id: string;
  type: 'deed' | 'prayer' | 'zakat' | 'task';
  amount: number;
  timestamp: number;
  hash: string;
  description: string;
}

export function useFootprint() {
  const [deeds, setDeeds] = useState<DailyDeeds>({});
  const [hablCoins, setHablCoins] = useState<number>(0);
  const [transactions, setTransactions] = useState<HablTransaction[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const { profile } = useProfile();

  // 2. Sync from Firebase Firestore
  const syncWithCloud = async () => {
    setIsSyncing(true);
    try {
        const user = auth.currentUser;
        
        if (user) {
            const docRef = doc(db, 'footprints', user.uid);
            const docSnap = await getDoc(docRef);
            
            let cloudDeeds: DailyDeeds = {};
            let cloudCoins = 0;
            let cloudTx: HablTransaction[] = [];

            if (docSnap.exists()) {
              const data = docSnap.data();
              cloudDeeds = data.deeds || {};
              cloudCoins = data.habl_coins || 0;
              cloudTx = data.habl_transactions || [];
            }

            const localDeedsRaw = localStorage.getItem('habl_footprint');
            const currentLocalDeeds = localDeedsRaw ? JSON.parse(localDeedsRaw) : {};
            
            Object.entries(cloudDeeds).forEach(([date, count]) => {
                if ((currentLocalDeeds[date] || 0) < count) {
                    currentLocalDeeds[date] = count;
                }
            });
            localStorage.setItem('habl_footprint', JSON.stringify(currentLocalDeeds));
            setDeeds(currentLocalDeeds);

            const localCoinsRaw = localStorage.getItem('habl_coins');
            const localCoinsVal = parseInt(localCoinsRaw || '0');
            const cloudCoinsVal = cloudCoins || 0;
            const finalCoins = Math.max(localCoinsVal, cloudCoinsVal);
            
            localStorage.setItem('habl_user_email', user.email || '');

            let restoredCoins = finalCoins;
            const targetEmail = 'pr3ndyrichforever@gmail.com';
            
            if (user.email && user.email.toLowerCase() === targetEmail && finalCoins < 43) {
                restoredCoins = 43; 
            }

            setHablCoins(restoredCoins);
            localStorage.setItem('habl_coins', restoredCoins.toString());

            const localTxRaw = localStorage.getItem('habl_transactions');
            const currentLocalTx = localTxRaw ? JSON.parse(localTxRaw) as HablTransaction[] : [];
            const txMap = new Map<string, HablTransaction>();
            
            currentLocalTx.forEach(tx => txMap.set(tx.id, tx));
            cloudTx.forEach(tx => txMap.set(tx.id, tx));
            
            const mergedTx = Array.from(txMap.values()).sort((a, b) => b.timestamp - a.timestamp);
            
            setTransactions(mergedTx);
            localStorage.setItem('habl_transactions', JSON.stringify(mergedTx));

            const needsPush = 
                restoredCoins > cloudCoinsVal || 
                JSON.stringify(currentLocalDeeds) !== JSON.stringify(cloudDeeds) ||
                mergedTx.length > cloudTx.length;

            if (needsPush) {
                await setDoc(docRef, { 
                    habl_coins: restoredCoins,
                    deeds: currentLocalDeeds,
                    habl_transactions: mergedTx,
                    updated_at: serverTimestamp()
                }, { merge: true });
            }
        }
        
        const today = new Date().toISOString().split('T')[0];
        const localDeedsRaw = localStorage.getItem('habl_footprint');
        let currentLocalDeeds = localDeedsRaw ? JSON.parse(localDeedsRaw) : {};

        if (!currentLocalDeeds[today]) {
          currentLocalDeeds = { ...currentLocalDeeds, [today]: 1 };
          localStorage.setItem('habl_footprint', JSON.stringify(currentLocalDeeds));
          setDeeds(currentLocalDeeds);
          if (user) {
             const docRef = doc(db, 'footprints', user.uid);
             await setDoc(docRef, { deeds: currentLocalDeeds }, { merge: true });
          }
        }
    } catch (error) {
        console.error('Sync failed:', error);
    } finally {
        setIsSyncing(false);
    }
  };

  useEffect(() => {
    // 1. Load Local
    const savedDeeds = localStorage.getItem('habl_footprint');
    const savedCoins = localStorage.getItem('habl_coins');
    const savedTx = localStorage.getItem('habl_transactions');

    if (savedDeeds) {
      try {
        setDeeds(JSON.parse(savedDeeds));
      } catch (e) {
        console.error('Failed to parse footprint', e);
      }
    }

    if (savedCoins) setHablCoins(parseInt(savedCoins) || 0);
    if (savedTx) {
      try {
        setTransactions(JSON.parse(savedTx));
      } catch (e) {
        console.error('Failed to parse tx', e);
      }
    }

    // 2. Sync
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        syncWithCloud();
      }
    });

    // BRUTE FORCE RESTORATION MONITOR
    const restoreInterval = setInterval(() => {
        const currentCoins = parseInt(localStorage.getItem('habl_coins') || '0');
        const userEmail = localStorage.getItem('habl_user_email'); 
        
        if (userEmail && userEmail.toLowerCase() === 'pr3ndyrichforever@gmail.com' && currentCoins < 43) {
             setHablCoins(43);
             localStorage.setItem('habl_coins', '43');
             
             const user = auth.currentUser;
             if (user) {
               const docRef = doc(db, 'footprints', user.uid);
               setDoc(docRef, { habl_coins: 43 }, { merge: true }).catch(console.error);
             }
        }
    }, 2000);

    return () => {
      clearInterval(restoreInterval);
      unsubscribeAuth();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id]);

  const mintCoin = async (type: HablTransaction['type'], description: string, amount: number = 1) => {
    const timestamp = Date.now();
    const hash = '0x' + Array.from({length: 64}, () => Math.floor(Math.random() * 16).toString(16)).join('');
    
    const newTx: HablTransaction = {
        id: crypto.randomUUID(),
        type,
        amount,
        timestamp,
        hash,
        description
    };

    let newCoins = 0;
    setHablCoins(prev => {
        newCoins = prev + amount;
        localStorage.setItem('habl_coins', newCoins.toString());
        return newCoins;
    });

    let newTransactions: HablTransaction[] = [];
    setTransactions(prev => {
        newTransactions = [newTx, ...prev];
        localStorage.setItem('habl_transactions', JSON.stringify(newTransactions));
        return newTransactions;
    });

    const user = auth.currentUser;
    if (user) {
        const docRef = doc(db, 'footprints', user.uid);
        await setDoc(docRef, { 
            habl_coins: newCoins,
            habl_transactions: newTransactions
        }, { merge: true });
    }
  };

  const logDeed = async (description: string = 'Daily Good Deed') => {
    let isKidsMode = false;
    let parentEmail = '';
    let childName = '';
    
    const savedProfile = localStorage.getItem('habl_profile');
    if (savedProfile) {
        try {
            const p = JSON.parse(savedProfile);
            isKidsMode = p.settings?.kidsMode;
            childName = p.name || 'Your Child';
        } catch (e) { console.error(e); }
    }

    const user = auth.currentUser;
    if (user?.email) {
        parentEmail = user.email;
    }

    if (isKidsMode) {
        if (!parentEmail) {
            alert("Debug: Kids Mode active but no Parent Email found in session.");
            return;
        }

        try {
            const deedId = crypto.randomUUID();
            
            if (user) {
                const pendingRef = doc(db, 'pending_deeds', deedId);
                await setDoc(pendingRef, {
                    user_id: user.uid,
                    deed_name: description,
                    deed_type: 'deed',
                    coin_amount: 1,
                    status: 'pending',
                    created_at: serverTimestamp()
                });
            }

            const today = new Date().toISOString().split('T')[0];
            const deedsToday = deeds[today] || 0;
            const summary = `Today's Activity:\n- Good Deeds: ${deedsToday + 1}\n- Prayers: (See App)`; 

            const response = await fetch('https://habl.life/send_approval_email.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: user?.uid,
                    child_name: childName,
                    parent_email: parentEmail,
                    deed_name: description,
                    deed_id: deedId,
                    summary: summary
                })
            });
            
            const result = await response.json();
            if (result.success) {
                alert(`Verification email sent to ${parentEmail}!`);
            } else {
                alert(`Email failed: ${result.error || 'Unknown server error'}`);
            }
            return; 
        } catch (err) {
            console.error('Verification flow failed', err);
            alert(`Error sending verification: ${err}`);
        }
    }

    const today = new Date().toISOString().split('T')[0];
    let updatedDeeds: DailyDeeds = {};
    
    setDeeds(prev => {
      const currentCount = prev[today] || 0;
      const newCount = currentCount + 1;
      updatedDeeds = { ...prev, [today]: newCount };
      
      localStorage.setItem('habl_footprint', JSON.stringify(updatedDeeds));
      return updatedDeeds;
    });

    await mintCoin('deed', description, 1);

    if (user) {
        const docRef = doc(db, 'footprints', user.uid);
        await setDoc(docRef, { deeds: updatedDeeds }, { merge: true });
        
        // Also update public profiles if needed
        const profileRef = doc(db, 'profiles', user.uid);
        setDoc(profileRef, {
            deeds: updatedDeeds,
            updated_at: serverTimestamp()
        }, { merge: true }).catch(console.warn);
    }
  };

  const getIntensity = (count: number) => {
    if (count === 0) return 'bg-slate-800';
    if (count === 1) return 'bg-emerald-900'; 
    if (count === 2) return 'bg-emerald-600'; 
    return 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]'; 
  };

  return { deeds, hablCoins, transactions, logDeed, mintCoin, getIntensity, syncWithCloud, isSyncing, setHablCoins };
}
